import React, { useState, useEffect } from 'react';
import { Palette, Upload, Save, Layout, Smartphone, Monitor, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

const SECTORS = [
  { value: 'construction', label: 'İnşaat' },
  { value: 'realestate', label: 'Emlak' },
  { value: 'architecture', label: 'Mimarlık' },
  { value: 'hotel', label: 'Otel / Konaklama' },
  { value: 'restaurant', label: 'Restoran / Kafe' },
  { value: 'clinic', label: 'Klinik / Sağlık' },
  { value: 'education', label: 'Eğitim' },
  { value: 'technology', label: 'Teknoloji' },
  { value: 'other', label: 'Diğer' },
];

const COLOR_PRESETS = [
  { name: 'Altın (Varsayılan)', primary: '#1a1d23', accent: '#d4950a', bg: '#faf8f5' },
  { name: 'Okyanus Mavisi', primary: '#0f172a', accent: '#0ea5e9', bg: '#f0f9ff' },
  { name: 'Zümrüt Yeşili', primary: '#0f1f1a', accent: '#10b981', bg: '#f0fdf4' },
  { name: 'Bordo Kırmızı', primary: '#1a0f0f', accent: '#dc2626', bg: '#fef2f2' },
  { name: 'Mor Lüks', primary: '#1a0f2e', accent: '#8b5cf6', bg: '#faf5ff' },
  { name: 'Turuncu Enerji', primary: '#1a1008', accent: '#f97316', bg: '#fff7ed' },
  { name: 'Gül Pembe', primary: '#1a0f14', accent: '#ec4899', bg: '#fdf2f8' },
  { name: 'Çelik Gri', primary: '#18181b', accent: '#71717a', bg: '#fafafa' },
  { name: 'Lacivert', primary: '#0c1222', accent: '#3b82f6', bg: '#eff6ff' },
  { name: 'Bakır', primary: '#1c1210', accent: '#b45309', bg: '#fffbeb' },
  { name: 'Turkuaz', primary: '#0f1a1a', accent: '#14b8a6', bg: '#f0fdfa' },
  { name: 'Şarap', primary: '#1a0d12', accent: '#be123c', bg: '#fff1f2' },
];

const HOMEPAGE_VERSIONS = [
  { value: 'v1', name: 'V1 - Standart', desc: 'Klasik slider + altında içerik bölümleri. Kurumsal görünüm.' },
  { value: 'v2', name: 'V2 - Tam Ekran', desc: 'Tam ekran slider, şeffaf navbar. Etkileyici ilk izlenim.' },
  { value: 'v3', name: 'V3 - Bölünmüş Ekran', desc: 'Sol tarafta metin, sağ tarafta görsel. Dengeli ve modern.' },
  { value: 'v4', name: 'V4 - Karanlık Minimal', desc: 'Koyu arka plan, büyük tipografi, minimal tasarım.' },
  { value: 'v5', name: 'V5 - Kart Grid', desc: 'Slider yerine proje kartları grid. Portföy odaklı görünüm.' },
  { value: 'v6', name: 'V6 - Galeri Şeridi', desc: 'Büyük görsel + alt kısımda küçük resim navigasyonu. Vitrin tarzı.' },
];

const SITE_TEMPLATES = [
  { id: 'corporate-gold', name: 'Kurumsal Altın', desc: 'Klasik kurumsal tasarım, altın vurgular, koyu tonlar.', homepage: 'v1', design: 'classic', accent: '#d4950a', primary: '#1a1d23', bg: '#faf8f5' },
  { id: 'modern-blue', name: 'Modern Mavi', desc: 'Temiz çizgiler, mavi tonlar, profesyonel teknoloji görünümü.', homepage: 'v2', design: 'atlas', accent: '#0ea5e9', primary: '#0f172a', bg: '#f0f9ff' },
  { id: 'minimal-dark', name: 'Minimal Karanlık', desc: 'Büyük tipografi, koyu arka plan, minimal ve şık.', homepage: 'v4', design: 'mono', accent: '#f97316', primary: '#0a0a0a', bg: '#fafafa' },
  { id: 'nature-green', name: 'Doğa Yeşili', desc: 'Yeşil tonlar, bölünmüş ekran, doğal ve ferah.', homepage: 'v3', design: 'clinic', accent: '#10b981', primary: '#0f1f1a', bg: '#f0fdf4' },
  { id: 'luxury-wine', name: 'Lüks Şarap', desc: 'Premium şarap kırmızısı, galeri şeridi, lüks hissiyat.', homepage: 'v6', design: 'luna', accent: '#be123c', primary: '#1a0d12', bg: '#fff1f2' },
  { id: 'portfolio-purple', name: 'Portföy Mor', desc: 'Mor vurgular, kart grid anasayfa, portföy odaklı.', homepage: 'v5', design: 'studio', accent: '#8b5cf6', primary: '#1a0f2e', bg: '#faf5ff' },
];

const DESIGN_THEMES = [
  { id: 'classic', name: 'Klasik', desc: 'Nötr, güvenli varsayılan.', fonts: 'Space Grotesk + IBM Plex Sans' },
  { id: 'atlas', name: 'Atlas', desc: 'İnşaat/mimarlık, keskin köşeler.', fonts: 'Montserrat + IBM Plex Sans' },
  { id: 'luna', name: 'Luna', desc: 'Otel/lüks, yumuşak hatlar.', fonts: 'Playfair Display + Figtree' },
  { id: 'bistro', name: 'Bistro', desc: 'Restoran, editorial tipografi.', fonts: 'Crimson Text + Montserrat' },
  { id: 'clinic', name: 'Klinik', desc: 'Sağlık, steril ve okunaklı.', fonts: 'Figtree + Source Serif' },
  { id: 'studio', name: 'Stüdyo', desc: 'Ajans/yaratıcı, cesur boşluk.', fonts: 'Space Grotesk + Crimson Text' },
  { id: 'mono', name: 'Monokrom', desc: 'Teknoloji, yüksek kontrast.', fonts: 'Roboto Mono + Figtree' },
  { id: 'play', name: 'Neşeli', desc: 'Eğitim/çocuk, yuvarlak hatlar.', fonts: 'Fredoka + Figtree' },
  { id: 'civic', name: 'Sivil', desc: 'Dernek/vakıf, güven veren.', fonts: 'IBM Plex Sans + Source Serif' },
  { id: 'verdict', name: 'Hüküm', desc: 'Hukuk/finans, otoriter.', fonts: 'Playfair Display + IBM Plex Sans' },
  { id: 'estate', name: 'Emlak', desc: 'Gayrimenkul, premium broşür.', fonts: 'Montserrat + Source Serif' },
  { id: 'atelier', name: 'Atölye', desc: 'Güzellik/kozmetik, zarif.', fonts: 'Crimson Text + Figtree' },
  { id: 'pulse', name: 'Nabız', desc: 'Spor/fitness, enerjik.', fonts: 'Space Grotesk + Roboto Mono' },
  { id: 'retail', name: 'Perakende', desc: 'E-ticaret, temiz ve hızlı.', fonts: 'Figtree + IBM Plex Sans' },
  { id: 'gazette', name: 'Gazete', desc: 'Moda/editorial, dergi ritmi.', fonts: 'Playfair Display + Crimson Text' },
  { id: 'gallery', name: 'Galeri', desc: 'Sanat/portfolyo, boşluklu.', fonts: 'Source Serif + Figtree' },
];

export default function ThemeEditor() {
  const [data, setData] = useState({ theme_colors: {}, sector: 'construction', favicon_path: '', og_image_path: '' });
  const [footerData, setFooterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [faviconFile, setFaviconFile] = useState(null);
  const [ogFile, setOgFile] = useState(null);
  const [previewMode, setPreviewMode] = useState('mobile');
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get('/theme'),
      api.get('/settings/footer')
    ]).then(([t, f]) => {
      setData(t.data);
      setFooterData(f.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let fav = data.favicon_path;
      let og = data.og_image_path;
      if (faviconFile) { const fd = new FormData(); fd.append('file', faviconFile); const r = await api.post('/uploads', fd); fav = r.data.path; }
      if (ogFile) { const fd = new FormData(); fd.append('file', ogFile); const r = await api.post('/uploads', fd); og = r.data.path; }
      await api.put('/theme', { ...data, favicon_path: fav, og_image_path: og });
      setData(d => ({ ...d, favicon_path: fav, og_image_path: og }));
      // Save homepage version to footer settings too
      if (footerData) {
        await api.put('/settings/footer', { ...footerData, homepage_version: footerData.homepage_version });
      }
      toast.success('Tema ayarları güncellendi. Değişikliklerin görünmesi için sayfayı yenileyin.');
      setPreviewKey(k => k + 1);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const applyPreset = (preset) => {
    setData(d => ({ ...d, theme_colors: { ...d.theme_colors, primary: preset.primary, accent: preset.accent, background: preset.bg } }));
  };

  const applyTemplate = (tmpl) => {
    setData(d => ({ ...d, theme_colors: { primary: tmpl.primary, accent: tmpl.accent, background: tmpl.bg, design_theme: tmpl.design } }));
    setFooterData(d => ({ ...d, homepage_version: tmpl.homepage }));
    toast.success(`"${tmpl.name}" şablonu uygulandı. Kaydet butonuna basmayı unutmayın.`);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>;

  const currentVersion = footerData?.homepage_version || 'v1';
  const currentDesign = data.theme_colors?.design_theme || 'classic';
  const setDesignTheme = (id) => setData(d => ({ ...d, theme_colors: { ...d.theme_colors, design_theme: id } }));

  return (
    <div data-testid="admin-theme-editor">
      <h1 className="font-heading text-2xl font-semibold mb-6">Tema & Görünüm</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Live Preview (mobile / desktop) */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-[hsl(var(--gold))]" /> Canlı Önizleme
            </h3>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-input overflow-hidden">
                <button type="button" onClick={() => setPreviewMode('mobile')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${previewMode === 'mobile' ? 'bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))]' : 'hover:bg-secondary'}`}
                  data-testid="preview-mode-mobile">
                  <Smartphone className="w-3.5 h-3.5" /> Mobil
                </button>
                <button type="button" onClick={() => setPreviewMode('desktop')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${previewMode === 'desktop' ? 'bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))]' : 'hover:bg-secondary'}`}
                  data-testid="preview-mode-desktop">
                  <Monitor className="w-3.5 h-3.5" /> Masaüstü
                </button>
              </div>
              <button type="button" onClick={() => setPreviewKey(k => k + 1)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-input hover:bg-secondary"
                data-testid="preview-refresh">
                <RefreshCw className="w-3.5 h-3.5" /> Yenile
              </button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Anasayfanın güncel görünümü. Değişiklikleri <strong>Kaydet</strong>'e bastıktan sonra önizleme otomatik yenilenir.
          </p>
          <div className="flex justify-center bg-secondary/40 rounded-xl p-4 overflow-hidden">
            {previewMode === 'mobile' ? (
              <div className="rounded-[2rem] border-[8px] border-gray-800 bg-gray-800 shadow-xl overflow-hidden" style={{ width: 390 }}>
                <iframe
                  key={previewKey}
                  title="Mobil Önizleme"
                  src="/"
                  className="block bg-white"
                  style={{ width: 374, height: 680, border: 'none' }}
                  data-testid="theme-preview-iframe"
                />
              </div>
            ) : (
              <div className="w-full rounded-xl border border-border overflow-hidden shadow-md">
                <iframe
                  key={previewKey}
                  title="Masaüstü Önizleme"
                  src="/"
                  className="block bg-white w-full"
                  style={{ height: 560, border: 'none' }}
                  data-testid="theme-preview-iframe"
                />
              </div>
            )}
          </div>
        </div>

        {/* Site Templates */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-2 flex items-center gap-2"><Palette className="w-5 h-5 text-[hsl(var(--gold))]" /> Hazır Şablonlar</h3>
          <p className="text-xs text-muted-foreground mb-4">Tek tıkla anasayfa versiyonu + renk paleti uygulayın</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SITE_TEMPLATES.map(tmpl => (
              <button key={tmpl.id} type="button" onClick={() => applyTemplate(tmpl)}
                className="p-4 rounded-xl border border-border hover:border-[hsl(var(--gold)/0.5)] text-left transition-all hover:shadow-md group">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex gap-1">
                    <div className="w-5 h-5 rounded-full border border-white/20" style={{ background: tmpl.primary }} />
                    <div className="w-5 h-5 rounded-full border border-white/20" style={{ background: tmpl.accent }} />
                    <div className="w-5 h-5 rounded-full border border-gray-300" style={{ background: tmpl.bg }} />
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{HOMEPAGE_VERSIONS.find(v => v.value === tmpl.homepage)?.name?.split(' - ')[1] || tmpl.homepage}</span>
                </div>
                <div className="font-heading font-semibold text-sm group-hover:text-[hsl(var(--gold))] transition-colors">{tmpl.name}</div>
                <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{tmpl.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Design Themes */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Palette className="w-5 h-5 text-[hsl(var(--gold))]" /> Tasarım Teması</h3>
            <a href="/tema-onizleme" target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-lg border border-input hover:bg-secondary" data-testid="open-theme-preview">Temaları Önizle ↗</a>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Sitenin tipografi, köşe, buton ve kart stilini belirler (renk ve anasayfadan bağımsız). Değişiklik kaydettikten sonra sitede görünür.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {DESIGN_THEMES.map(t => (
              <button key={t.id} type="button" onClick={() => setDesignTheme(t.id)} data-testid={`design-theme-${t.id}`}
                className={`p-4 rounded-xl border-2 text-left transition-colors ${currentDesign === t.id ? 'border-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.05)]' : 'border-border hover:border-border/80'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${currentDesign === t.id ? 'border-[hsl(var(--gold))]' : 'border-border'}`}>
                    {currentDesign === t.id && <div className="w-2 h-2 rounded-full bg-[hsl(var(--gold))]" />}
                  </div>
                  <span className="font-heading font-semibold text-sm">{t.name}</span>
                </div>
                <p className="text-[11px] text-muted-foreground">{t.desc}</p>
                <p className="text-[10px] text-muted-foreground mt-1 font-mono">{t.fonts}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Homepage Version */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Layout className="w-5 h-5 text-[hsl(var(--gold))]" /> Anasayfa Versiyonu</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {HOMEPAGE_VERSIONS.map(v => (
              <button key={v.value} type="button"
                onClick={() => setFooterData(d => ({ ...d, homepage_version: v.value }))}
                className={`p-4 rounded-xl border-2 text-left transition-colors ${
                  currentVersion === v.value ? 'border-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.05)]' : 'border-border hover:border-border/80'
                }`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    currentVersion === v.value ? 'border-[hsl(var(--gold))]' : 'border-border'
                  }`}>
                    {currentVersion === v.value && <div className="w-2 h-2 rounded-full bg-[hsl(var(--gold))]" />}
                  </div>
                  <span className="font-heading font-semibold text-sm">{v.name}</span>
                </div>
                <p className="text-[11px] text-muted-foreground ml-6">{v.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Sector */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">Sektör</h3>
          <select value={data.sector} onChange={e => setData(d => ({ ...d, sector: e.target.value }))}
            className="w-full max-w-xs px-3 py-2.5 rounded-lg border border-input bg-background text-sm">
            {SECTORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {/* Color Presets */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Palette className="w-5 h-5 text-[hsl(var(--gold))]" /> Renk Paleti</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {COLOR_PRESETS.map(p => (
              <button key={p.name} type="button" onClick={() => applyPreset(p)}
                className={`p-3 rounded-xl border-2 text-left transition-colors ${
                  data.theme_colors?.accent === p.accent ? 'border-[hsl(var(--gold))]' : 'border-border hover:border-border/80'
                }`}>
                <div className="flex gap-1 mb-2">
                  <div className="w-6 h-6 rounded-full" style={{ background: p.primary }} />
                  <div className="w-6 h-6 rounded-full" style={{ background: p.accent }} />
                  <div className="w-6 h-6 rounded-full border" style={{ background: p.bg }} />
                </div>
                <span className="text-[10px] font-medium">{p.name}</span>
              </button>
            ))}
          </div>
          <h4 className="text-sm font-medium mb-3">Özel Renkler</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <ColorPicker label="Ana Renk (Primary)" value={data.theme_colors?.primary || '#1a1d23'} onChange={v => setData(d => ({ ...d, theme_colors: { ...d.theme_colors, primary: v } }))} />
            <ColorPicker label="Vurgu Rengi (Accent)" value={data.theme_colors?.accent || '#d4950a'} onChange={v => setData(d => ({ ...d, theme_colors: { ...d.theme_colors, accent: v } }))} />
            <ColorPicker label="Arka Plan" value={data.theme_colors?.background || '#faf8f5'} onChange={v => setData(d => ({ ...d, theme_colors: { ...d.theme_colors, background: v } }))} />
          </div>
        </div>

        {/* Favicon & OG Image */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">Favicon & Sosyal Medya Görseli</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1.5">Favicon (32x32 veya 64x64 px, PNG/ICO)</label>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary">
                <Upload className="w-4 h-4" /> Favicon Yükle
                <input type="file" accept="image/*" className="hidden" onChange={e => setFaviconFile(e.target.files[0])} />
              </label>
              {data.favicon_path && <img src={getFileUrl(data.favicon_path)} alt="" className="mt-2 h-8 w-8" />}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">OG Image (1200x630 px, sosyal medya paylaşımı)</label>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary">
                <Upload className="w-4 h-4" /> OG Image Yükle
                <input type="file" accept="image/*" className="hidden" onChange={e => setOgFile(e.target.files[0])} />
              </label>
              {data.og_image_path && <img src={getFileUrl(data.og_image_path)} alt="" className="mt-2 h-16 rounded-lg" />}
            </div>
          </div>
        </div>

        <button type="submit" disabled={isSubmitting}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50">
          <Save className="w-4 h-4" /> {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </form>
    </div>
  );
}

const ColorPicker = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5">{label}</label>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-10 h-10 rounded-lg border border-input cursor-pointer" />
      <input type="text" value={value} onChange={e => onChange(e.target.value)} className="flex-1 px-3 py-2.5 rounded-lg border border-input bg-background text-sm font-mono" />
    </div>
  </div>
);
